import React, { useState, useEffect, useRef, useCallback } from 'react';
import { 
    Box, 
    Grid, 
    Button, 
    Text, 
    VStack, 
    HStack, 
    Modal, 
    ModalOverlay, 
    ModalContent, 
    ModalHeader, 
    ModalBody, 
    ModalFooter,
    Flex,
    Badge,
    useDisclosure,
    useColorModeValue
} from '@chakra-ui/react';
import { keyframes } from '@emotion/react';
import { motion } from 'framer-motion';
import { FaCoins } from 'react-icons/fa';
import { useGame } from '../../context/GameContext';

const ChestBonus = ({ isOpen, onClose, onWin }) => {
    const [chests, setChests] = useState([]);
    const [picksRemaining, setPicksRemaining] = useState(3);
    const [selectedChests, setSelectedChests] = useState([]);
    const [revealedChests, setRevealedChests] = useState([]);
    const [totalWinAmount, setTotalWinAmount] = useState(0);
    const [isRevealing, setIsRevealing] = useState(false);
    const chestRef = useRef(null);
    
    const { gameState } = useGame();
    const { betAmount = 1 } = gameState;
    
    // Multipliers for the chests
    const multipliers = [1, 1.5, 2, 2.5, 3, 5, 7, 10, 15];
    
    // Gift emojis to use
    const giftEmojis = ['🎁', '🎀', '🧸', '🎮', '💎', '👑', '🏆', '💰', '🔮'];
    
    // Initialize chests with random multipliers
    useEffect(() => {
        if (isOpen) {
            const shuffledMultipliers = [...multipliers].sort(() => Math.random() - 0.5);
            const shuffledEmojis = [...giftEmojis].sort(() => Math.random() - 0.5);
            
            const newChests = shuffledMultipliers.map((multiplier, index) => ({
                id: index,
                multiplier,
                emoji: shuffledEmojis[index],
                revealed: false
            }));
            
            setChests(newChests);
            setPicksRemaining(3);
            setSelectedChests([]);
            setRevealedChests([]);
            setTotalWinAmount(0);
            setIsRevealing(false);
        }
    }, [isOpen]);
    
    const launchConfetti = useCallback((element) => {
        // Check if element exists before accessing getBoundingClientRect
        if (!element) return;
        
        try {
            const rect = element.getBoundingClientRect();
            // Check if rect has the required properties
            if (!rect) return;
            
            const x = (rect.left + rect.width / 2) / window.innerWidth; // center of the chest
            const y = rect.top / window.innerHeight; // top of the chest

            import('canvas-confetti').then(({ default: confetti }) => {
                confetti({
                    origin: { x, y },
                    particleCount: 200,
                    spread: 80,
                    startVelocity: 30,
                    gravity: 1,
                    ticks: 200,
                    zIndex: 9999,
                    colors: [
                        '#FFD700',  // gold
                        '#FFA500',  // orange
                        '#FF0000',  // red
                        '#800080',  // purple
                        '#FFFFFF',  // white
                    ]
                });
            }).catch(err => {
                console.error("Error loading confetti:", err);
            });
        } catch (error) {
            console.error("Error in launchConfetti:", error);
        }
    }, []);
    
    const handleChestSelect = (chestId) => {
        // Don't allow selection if already selected or revealing
        if (selectedChests.includes(chestId) || isRevealing || picksRemaining <= 0) return;
        
        setSelectedChests(prev => [...prev, chestId]);
        setIsRevealing(true);
        setPicksRemaining(prev => prev - 1);
        
        // Simulate opening animation
        setTimeout(() => {
            const chest = chests.find(c => c.id === chestId);
            setRevealedChests(prev => [...prev, chest]);
            
            const win = chest.multiplier * betAmount;
            setTotalWinAmount(prev => prev + win);
            
            // Launch confetti
            if (chestRef.current) {
                launchConfetti(chestRef.current);
            }
            
            setIsRevealing(false);
            
            // Call the onWin callback only when all picks are used or when user collects
            if (picksRemaining === 1) {
                if (onWin) {
                    setTimeout(() => {
                        onWin(totalWinAmount + win);
                    }, 1000);
                }
            }
        }, 1500);
    };
    
    const glowAnimation = keyframes`
      0% { box-shadow: 0 0 10px rgba(255, 215, 0, 0.5); }
      50% { box-shadow: 0 0 20px rgba(255, 215, 0, 0.8); }
      100% { box-shadow: 0 0 10px rgba(255, 215, 0, 0.5); }
    `;
    
    return (
        <Modal isOpen={isOpen} onClose={onClose} size="xl" isCentered>
            <ModalOverlay backdropFilter="blur(10px)" />
            <ModalContent bg="gray.800" borderRadius="xl" boxShadow="0 0 30px rgba(0, 0, 0, 0.5)">
                <ModalHeader 
                    bg="purple.800" 
                    color="white" 
                    borderTopRadius="xl"
                    textAlign="center"
                    fontSize="2xl"
                >
                    Treasure Gift Bonus Game
                </ModalHeader>
                
                <ModalBody p={6}>
                    <VStack spacing={6}>
                        <HStack spacing={4} width="100%" justifyContent="center">
                            <Badge colorScheme="purple" fontSize="md" p={2} borderRadius="md">
                                Picks Remaining: {picksRemaining}
                            </Badge>
                            
                            <Badge colorScheme="green" fontSize="md" p={2} borderRadius="md">
                                Total Win: {totalWinAmount.toFixed(2)} JINU
                            </Badge>
                        </HStack>
                        
                        <Text fontSize="lg" textAlign="center" color="yellow.300">
                            Select {picksRemaining} {picksRemaining === 1 ? 'gift' : 'gifts'} to reveal your prizes! Choose wisely...
                        </Text>
                        
                        <Grid templateColumns="repeat(3, 1fr)" gap={4} width="100%">
                            {chests.map((chest) => {
                                const isSelected = selectedChests.includes(chest.id);
                                const isRevealed = revealedChests.some(c => c.id === chest.id);
                                
                                return (
                                    <Box
                                        key={chest.id}
                                        bg={isRevealed ? "yellow.400" : "purple.700"}
                                        p={4}
                                        borderRadius="md"
                                        cursor={!isSelected && !isRevealing && picksRemaining > 0 ? "pointer" : "default"}
                                        onClick={() => handleChestSelect(chest.id)}
                                        display="flex"
                                        justifyContent="center"
                                        alignItems="center"
                                        height="100px"
                                        boxShadow="0 4px 8px rgba(0,0,0,0.2)"
                                        _hover={{
                                            bg: !isSelected && !isRevealing && picksRemaining > 0 ? "purple.600" : undefined,
                                            transform: !isSelected && !isRevealing && picksRemaining > 0 ? "translateY(-5px)" : undefined
                                        }}
                                        animation={isSelected && !isRevealed ? 
                                            `${glowAnimation} 2s infinite` : "none"}
                                        transition="all 0.3s"
                                        ref={isSelected && !isRevealed ? chestRef : null}
                                    >
                                        {isRevealed ? (
                                            <VStack spacing={1}>
                                                <Text fontSize="xl" fontWeight="bold" color="purple.900">
                                                    {chest.multiplier}×
                                                </Text>
                                                <FaCoins size="30px" color="gold" />
                                            </VStack>
                                        ) : (
                                            <Box 
                                                as={motion.div}
                                                animate={
                                                    isSelected && isRevealing ? 
                                                    { rotateY: 180, scale: [1, 1.1, 1] } : 
                                                    {}
                                                }
                                                transition={{ duration: 1.5 }}
                                                fontSize="4xl"
                                            >
                                                {chest.emoji}
                                            </Box>
                                        )}
                                    </Box>
                                );
                            })}
                        </Grid>
                        
                        {revealedChests.length > 0 && (
                            <Box 
                                mt={4} 
                                p={4} 
                                bg="green.800" 
                                borderRadius="lg" 
                                w="100%"
                                textAlign="center"
                            >
                                <Text fontSize="lg" color="white" mb={2}>
                                    Total Winnings:
                                </Text>
                                <Text fontSize="3xl" fontWeight="bold" color="yellow.300">
                                    {totalWinAmount.toFixed(2)} JINU
                                </Text>
                                <HStack spacing={2} justifyContent="center" mt={2}>
                                    {revealedChests.map((chest, index) => (
                                        <Badge key={index} colorScheme="yellow" p={1}>
                                            {chest.multiplier}×
                                        </Badge>
                                    ))}
                                </HStack>
                            </Box>
                        )}
                    </VStack>
                </ModalBody>
                
                <ModalFooter>
                    <Button 
                        colorScheme="purple" 
                        mr={3} 
                        onClick={() => {
                            if (picksRemaining < 3 && onWin) {
                                onWin(totalWinAmount);
                            }
                            onClose();
                        }}
                        isDisabled={isRevealing}
                    >
                        {picksRemaining === 0 || revealedChests.length > 0 ? "Collect Winnings" : "Cancel"}
                    </Button>
                </ModalFooter>
            </ModalContent>
        </Modal>
    );
};

export default ChestBonus;
